'use client';

import type { FC } from 'react';
import { useDisplayUserName } from '../../hooks/useDisplayUserName';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { formatDayMonth } from '../../i18n/formatDate';
import { pluralize } from '../../i18n/pluralize';
import { cn } from '../../utils/cn';
import { extractSkinName } from '../../utils/formatters';
import { rarityGradient } from '../../utils/rarityGradient';
import { Credits } from '../Credits';
import { GiOpenChest } from '../icons';
import { RainCaseBackdrop } from './RainCaseBackdrop';
import { BlurImage } from '../ui/BlurImage';
import { SkinImage } from '../ui/SkinImage';
import translations from './RainWaitingCard.translations';
import type { RainHistoryEntry } from './types';

export interface RainWaitingCardProps {
  lastRound?: RainHistoryEntry | null;
  onOpen?: () => void;
  className?: string;
}

export const RainWaitingCard: FC<RainWaitingCardProps> = ({ lastRound, onOpen, className }) => {
  const { t, lang } = useLocalTranslation(translations);
  const displayUserName = useDisplayUserName();

  const round = lastRound && lastRound.winners.length > 0 ? lastRound : null;
  const sourceCase = round?.case;
  const winner = round?.winners[0];
  const extraWinners = round ? round.winners.length - 1 : 0;

  const timeAgo = (iso: string): string => {
    const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (minutes < 1) return t('justNow');
    if (minutes === 1) return t('minuteAgo');
    if (minutes < 60) {
      return pluralize(
        minutes,
        {
          one: t('minutesAgoOne', { count: minutes }),
          few: t('minutesAgoFew', { count: minutes }),
          many: t('minutesAgoMany', { count: minutes }),
        },
        lang,
      );
    }
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return t('hourAgo');
    if (hours < 24) {
      return pluralize(
        hours,
        {
          one: t('hoursAgoOne', { count: hours }),
          few: t('hoursAgoFew', { count: hours }),
          many: t('hoursAgoMany', { count: hours }),
        },
        lang,
      );
    }
    return formatDayMonth(iso, lang);
  };

  const body = (
    <div className="relative z-[1] flex flex-col gap-2 p-3">
      <div className="flex items-center gap-3">

        <div className="size-[76px] shrink-0 overflow-hidden rounded-xl bg-white/[0.06]">
          {sourceCase ? (
            <BlurImage
              src={sourceCase.imageUrl}
              placeholder={sourceCase.imagePlaceholder}
              alt={sourceCase.name}
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <GiOpenChest className="text-3xl text-white/30" />
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
          {round && winner ? (
            <>
              <div className="truncate text-xs text-white/60">
                {t('lastRoundLabel')} · {timeAgo(round.finalizedAt)}
              </div>

              <div className="truncate text-sm font-semibold text-white">
                {displayUserName(winner.firstName, winner.lastName)}
              </div>
              <div className="flex items-center gap-2">

                <div
                  className="relative size-6 shrink-0 overflow-hidden rounded-md"
                  style={{ background: rarityGradient(winner.item.rarityColor) }}
                >
                  <SkinImage
                    name={winner.item.hashName}
                    src={winner.item.imageUrl}
                    alt={winner.item.name}
                    className="absolute inset-0"
                    imgClassName="h-full w-full object-contain p-px"
                  />
                </div>

                <span className="truncate text-sm text-white/80">{extractSkinName(winner.item.name)}</span>
                <span className="shrink-0 text-xs text-white/60">
                  <Credits amount={winner.item.priceInCredits} iconSize={11} />
                </span>
              </div>
              {extraWinners > 0 && (
                <div className="truncate text-xs text-white/50">
                  {pluralize(
                    extraWinners,
                    {
                      one: t('moreWinnersOne', { count: extraWinners }),
                      few: t('moreWinnersFew', { count: extraWinners }),
                      many: t('moreWinnersMany', { count: extraWinners }),
                    },
                    lang,
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="truncate text-sm font-semibold text-white">{t('whatIsItTitle')}</div>
              <div className="text-sm text-white/70">{t('whatIsIt')}</div>
            </>
          )}
        </div>
      </div>

      <div className="text-xs text-white/50">{t('note')}</div>
    </div>
  );

  const shell = cn(
    'relative w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-fill-tertiary text-start text-white',
    className,
  );

  const backdrop = sourceCase && (
    <RainCaseBackdrop imageUrl={sourceCase.imageUrl} imagePlaceholder={sourceCase.imagePlaceholder} />
  );

  if (!onOpen || !round) {
    return (
      <div className={shell}>
        {backdrop}
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-sound="menu_select"
      onClick={onOpen}
      className={cn(shell, 'transition active:opacity-80')}
    >
      {backdrop}
      {body}
    </button>
  );
};
